/**
 * What the PDF renderer draws from — decoupled from the editor's `Plan` so the
 * renderer is testable with hand-built rooms. Port of the dataclasses at the top
 * of `backend/app/services/pdf.py` and of `_build_render_plan` in
 * `api/v1/exports.py`.
 */

import type { EventKind, Plan, SeatLabelMode, TableColor, TableShape } from '../types'

/** A guest photo, already fetched: JPEG bytes and the pixel size they decode to. */
export interface PhotoRef {
  /** Stable identity (the asset id), so one photo is embedded once. */
  key: string
  jpeg: Uint8Array
  width: number
  height: number
}

export interface RenderSeat {
  position: number
  guestName: string | null
  photo: PhotoRef | null
}

export interface RenderTable {
  id: string
  name: string
  shape: TableShape
  color: TableColor
  x: number
  y: number
  rotation: number
  seats: RenderSeat[]
}

export interface RenderPlan {
  title: string
  /** Only the file's metadata asks. Absent — test rooms, the parity plan — is a wedding. */
  eventKind?: EventKind
  /** YYYY-MM-DD */
  eventDate: string | null
  venue: string | null
  tables: RenderTable[]
  unseated: string[]
  /**
   * How chairs are drawn. On the plan rather than a renderer argument because it
   * changes how much room a chair needs, and so how many pages the room takes.
   */
  seatLabels: SeatLabelMode
  /** Orders the find-your-seat index (and was used for `unseated`). Code points when absent. */
  compareNames?: (a: string, b: string) => number
}

export const seatCountOf = (table: RenderTable): number => table.seats.length

export const occupiedOf = (table: RenderTable): number =>
  table.seats.reduce((sum, seat) => sum + (seat.guestName ? 1 : 0), 0)

export const totalSeats = (plan: RenderPlan): number =>
  plan.tables.reduce((sum, table) => sum + seatCountOf(table), 0)

export const totalGuests = (plan: RenderPlan): number =>
  plan.tables.reduce((sum, table) => sum + occupiedOf(table), 0)

/** Python's `sorted(key=str.casefold)`, close enough: lower-cased code-unit order. */
export function byCasefold(a: string, b: string): number {
  const x = a.toLowerCase()
  const y = b.toLowerCase()
  return x < y ? -1 : x > y ? 1 : 0
}

/**
 * How the waiting list and the find-your-seat index order names.
 *
 * - `reader` — the plan language's alphabet (`Intl.Collator`): Çingiz after
 *   Cavid and Xəyal after Həsən in an Azerbaijani plan, Ömer after Oğuz in a
 *   Turkish one, Chinese names by pinyin. What the app prints.
 * - `codepoint` — what the server printed (Python's `str.casefold` order), which
 *   put every Ç, Ə, Ö, Ş and Ü after Z: a guest looking for "Əliyeva" on the sign
 *   at the door would not find her where the alphabet says. Kept so the parity
 *   script can still compare pages with the server's.
 */
export type NameOrder = 'reader' | 'codepoint'

export function compareNames(language: string, order: NameOrder): (a: string, b: string) => number {
  if (order === 'codepoint') return byCasefold
  const collator = new Intl.Collator(language, { sensitivity: 'base', numeric: true })
  // Names the alphabet calls equal ("Chloé", "Chloe") still get one fixed order.
  return (a, b) => collator.compare(a, b) || byCasefold(a, b)
}

/**
 * Project the editor's plan into the renderer's model.
 *
 * Tables in stacking order, chairs by position. A chair's printed name is its
 * guest's — never a stored chair label, which the editor cannot show, so it
 * would print on a chair the planner sees as empty. Anyone not in a chair goes
 * on the waiting list, alphabetically.
 */
export function renderPlanFromPlan(
  plan: Plan,
  options: { photos?: Map<string, PhotoRef>; seatLabels?: SeatLabelMode; nameOrder?: NameOrder } = {},
): RenderPlan {
  const order = compareNames(plan.language, options.nameOrder ?? 'reader')
  const guests = new Map(plan.guests.map((guest) => [guest.id, guest]))
  const seated = new Set<string>()

  const tables: RenderTable[] = [...plan.tables]
    .map((table, order) => ({ table, order }))
    .sort((a, b) => a.table.zIndex - b.table.zIndex || a.order - b.order)
    .map(({ table }) => ({
      id: table.id,
      name: table.name,
      shape: table.shape,
      color: table.color,
      x: table.x,
      y: table.y,
      rotation: table.rotation,
      seats: [...table.seats]
        .sort((a, b) => a.position - b.position)
        .map((seat) => {
          const guest = seat.guestId ? guests.get(seat.guestId) : undefined
          if (guest) seated.add(guest.id)
          return {
            position: seat.position,
            guestName: guest ? guest.fullName : null,
            photo:
              guest?.photoAssetId && options.photos
                ? (options.photos.get(guest.photoAssetId) ?? null)
                : null,
          }
        }),
    }))

  const unseated = plan.guests
    .filter((guest) => !seated.has(guest.id))
    .map((guest) => guest.fullName)
    .sort(order)

  return {
    title: plan.title,
    eventKind: plan.eventKind,
    eventDate: plan.eventDate,
    venue: plan.venue,
    tables,
    unseated,
    seatLabels: options.seatLabels ?? plan.settings.seatLabels,
    compareNames: order,
  }
}
